import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentTransactions } from '@/api/paymentTransactions';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import type { PaymentTransactionRow, PaymentStatus, ListParams } from '@/api/paymentTransactions';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DateTimePicker } from '@/components/DateTimePicker';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/cn';
import {
  CheckCircle2, XCircle, RotateCcw, Receipt, ChevronLeft, ChevronRight,
  Download, Undo2,
} from 'lucide-react';

const STATUS_STYLES: Record<PaymentStatus, { label: string; className: string; icon: typeof CheckCircle2 }> = {
  captured: { label: 'Captured', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  declined: { label: 'Declined', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  refunded: { label: 'Refunded', className: 'bg-sky-50 text-sky-700 border-sky-200', icon: RotateCcw },
  partially_refunded: { label: 'Partial refund', className: 'bg-sky-50 text-sky-700 border-sky-200', icon: RotateCcw },
  initiated: { label: 'Pending', className: 'bg-slate-50 text-slate-500 border-slate-200', icon: Receipt },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

function StatusPill({ status }: { status: PaymentStatus }) {
  const s = STATUS_STYLES[status];
  const Icon = s.icon;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none',
      s.className,
    )}>
      <Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
}

function formatMoney(cents: number, currency = 'USD'): string {
  const dollars = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(dollars);
  } catch {
    return `${currency} ${dollars.toFixed(2)}`;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export function ChargeHistory() {
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [refundTarget, setRefundTarget] = useState<PaymentTransactionRow | null>(null);
  const limit = 50;

  const params: ListParams = {
    limit, offset,
    status: statusFilter === 'all' ? undefined : statusFilter,
    from: fromDate ? new Date(fromDate).toISOString() : undefined,
    to: toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined,
    customer_id: effectiveCustomerId ?? undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['paymentTransactions', params],
    queryFn: () => paymentTransactions.list(params),
  });

  // Client-side filter by caller display name / gateway name / card last4 /
  // node id (server lacks a search column — these are small pages).
  const filteredRows = (data?.rows ?? []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.caller_display_name ?? '').toLowerCase().includes(q) ||
      (r.gateway_name ?? '').toLowerCase().includes(q) ||
      (r.gateway_ref ?? '').toLowerCase().includes(q) ||
      (r.error_code ?? '').toLowerCase().includes(q)
    );
  });

  const summary = data?.summary;
  const hasMore = data ? offset + data.rows.length < data.total : false;

  const columns = [
    { header: 'Date', cell: (r: PaymentTransactionRow) => (
      <span className="text-xs text-slate-600 font-mono">{formatDate(r.created_at)}</span>
    ) },
    { header: 'Amount', cell: (r: PaymentTransactionRow) => (
      <span className={cn('font-mono text-sm tabular-nums', r.status === 'captured' ? 'text-emerald-700 font-semibold' : 'text-slate-500')}>
        {formatMoney(r.amount_cents, r.currency)}
      </span>
    ) },
    { header: 'Status', cell: (r: PaymentTransactionRow) => <StatusPill status={r.status} /> },
    { header: 'Caller', cell: (r: PaymentTransactionRow) => (
      r.caller_display_name
        ? <span className="text-sm text-slate-700">{r.caller_display_name}</span>
        : <span className="text-xs text-slate-400">Anonymous</span>
    ) },
    { header: 'Gateway', cell: (r: PaymentTransactionRow) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-sm text-slate-700">{r.gateway_name ?? '—'}</span>
        {r.gateway_kind && (
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{r.gateway_kind}</span>
        )}
      </div>
    ) },
    { header: 'Reference', cell: (r: PaymentTransactionRow) => (
      r.gateway_ref
        ? <span className="font-mono text-[11px] text-slate-500 max-w-[180px] truncate inline-block" title={r.gateway_ref}>{r.gateway_ref}</span>
        : <span className="text-xs text-slate-300">—</span>
    ) },
    { header: 'Error', cell: (r: PaymentTransactionRow) => (
      r.status === 'declined' && r.error_code ? (
        <span className="font-mono text-[11px] text-red-600" title={r.error_message ?? undefined}>
          {r.error_code}
        </span>
      ) : <span className="text-xs text-slate-300">—</span>
    ) },
    { header: 'Actions', className: 'w-24', cell: (r: PaymentTransactionRow) => {
      // Refund button only for captured / partially_refunded rows.
      const refundable =
        (r.status === 'captured' || r.status === 'partially_refunded') &&
        r.amount_cents - (r.refunded_amount_cents ?? 0) > 0;
      if (!refundable) return <span className="text-xs text-slate-300">—</span>;
      return (
        <Button
          size="sm" variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => setRefundTarget(r)}
          title={r.refunded_amount_cents > 0
            ? `${formatMoney(r.amount_cents - r.refunded_amount_cents, r.currency)} remaining`
            : 'Refund this charge'}
        >
          <Undo2 className="h-3 w-3 me-1" />
          Refund
        </Button>
      );
    } },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Charge history"
        description="Every payment attempt processed through your IVR payment nodes — success or decline."
        actions={
          <Button
            variant="outline" size="sm"
            onClick={() => {
              paymentTransactions.downloadCsv(params).catch((err) => {
                toast({
                  title: 'CSV download failed',
                  description: err instanceof Error ? err.message : 'unknown',
                  variant: 'destructive',
                });
              });
            }}
            title="Download CSV of the current filtered view"
          >
            <Download className="h-4 w-4 me-1.5" />
            Export CSV
          </Button>
        }
      />

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <SummaryCard
          label="Captured"
          value={summary ? formatMoney(summary.captured_cents) : '—'}
          sublabel={summary ? `${summary.captured_count} transaction${summary.captured_count === 1 ? '' : 's'}` : ''}
          accent="emerald"
        />
        <SummaryCard
          label="Declined"
          value={summary ? String(summary.declined_count) : '—'}
          sublabel={summary && summary.transactions_total > 0
            ? `${Math.round(summary.declined_count * 100 / summary.transactions_total)}% of attempts`
            : ''}
          accent="red"
        />
        <SummaryCard
          label="Total attempts"
          value={summary ? String(summary.transactions_total) : '—'}
          sublabel={fromDate || toDate ? 'In filter window' : 'All time'}
          accent="slate"
        />
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 min-w-[160px]">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="ch-search">Search</label>
              <Input
                id="ch-search" value={search}
                onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                placeholder="Caller, gateway, ref..."
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as typeof statusFilter); setOffset(0); }}>
                <SelectTrigger className="h-9 w-40 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="captured">Captured</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="initiated">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground block">From</span>
              <DateTimePicker
                value={fromDate ? `${fromDate}T00:00` : ''}
                onChange={(v) => { setFromDate(v ? v.slice(0, 10) : ''); setOffset(0); }}
                defaultTimeOnDateSelect="00:00"
                placeholder="Start"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground block">To</span>
              <DateTimePicker
                value={toDate ? `${toDate}T23:59` : ''}
                onChange={(v) => { setToDate(v ? v.slice(0, 10) : ''); setOffset(0); }}
                defaultTimeOnDateSelect="23:59"
                placeholder="End"
              />
            </div>
            {(search || statusFilter !== 'all' || fromDate || toDate) && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setSearch(''); setStatusFilter('all'); setFromDate(''); setToDate(''); setOffset(0); }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading transactions…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load transactions.'}
        </div>
      ) : (
        <>
          <DataTable
            rows={filteredRows}
            columns={columns}
            empty={
              search ? `No transactions match "${search}".`
              : statusFilter !== 'all' ? `No ${statusFilter} transactions in this window.`
              : 'No payment transactions yet. Payments processed through IVR payment nodes will appear here.'
            }
          />

          {/* Pagination */}
          {data && data.total > limit && (
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Showing {offset + 1}–{offset + filteredRows.length} of {data.total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                  Prev
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={!hasMore}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {refundTarget && (
        <RefundDialog
          tx={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={() => {
            setRefundTarget(null);
            qc.invalidateQueries({ queryKey: ['paymentTransactions'] });
            toast({ title: 'Refund processed' });
          }}
          onError={(msg) => {
            toast({
              title: 'Refund failed',
              description: msg,
              variant: 'destructive',
            });
          }}
        />
      )}
    </>
  );
}

function RefundDialog({
  tx, onClose, onSuccess, onError,
}: {
  tx: PaymentTransactionRow;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const remaining = tx.amount_cents - (tx.refunded_amount_cents ?? 0);
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [partialDollars, setPartialDollars] = useState(
    (remaining / 100).toFixed(2),
  );
  const [reason, setReason] = useState('');

  const refund = useMutation({
    mutationFn: () => {
      const body =
        mode === 'full'
          ? { reason: reason || undefined }
          : {
              amount_cents: Math.round(parseFloat(partialDollars) * 100),
              reason: reason || undefined,
            };
      return paymentTransactions.refund(tx.id, body);
    },
    onSuccess,
    onError: (err) => onError(err instanceof Error ? err.message : 'unknown'),
  });

  const partialAmount = Math.round(parseFloat(partialDollars) * 100);
  const partialValid =
    !isNaN(partialAmount) && partialAmount > 0 && partialAmount <= remaining;
  const canSubmit = mode === 'full' || partialValid;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded border border-border p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original:</span>
              <span className="font-mono">{formatMoney(tx.amount_cents, tx.currency)}</span>
            </div>
            {tx.refunded_amount_cents > 0 && (
              <div className="flex justify-between text-sky-700">
                <span>Already refunded:</span>
                <span className="font-mono">{formatMoney(tx.refunded_amount_cents, tx.currency)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>Remaining:</span>
              <span className="font-mono">{formatMoney(remaining, tx.currency)}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio" name="refund-mode" value="full"
                checked={mode === 'full'}
                onChange={() => setMode('full')}
              />
              Full refund ({formatMoney(remaining, tx.currency)})
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio" name="refund-mode" value="partial"
                checked={mode === 'partial'}
                onChange={() => setMode('partial')}
              />
              Partial
            </label>
          </div>

          {mode === 'partial' && (
            <div className="space-y-1">
              <Label htmlFor="rd-partial">Amount ({tx.currency})</Label>
              <Input
                id="rd-partial"
                type="number" step="0.01" min="0.01"
                max={(remaining / 100).toFixed(2)}
                value={partialDollars}
                onChange={(e) => setPartialDollars(e.target.value)}
                className="font-mono"
              />
              {!partialValid && (
                <p className="text-xs text-destructive">
                  Must be between $0.01 and {formatMoney(remaining, tx.currency)}.
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="rd-reason">Reason (optional)</Label>
            <Input
              id="rd-reason" value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Customer request, duplicate charge"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            This calls the gateway directly. Funds typically appear back on
            the cardholder's statement in 5–10 business days.
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={refund.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => refund.mutate()}
            disabled={refund.isPending || !canSubmit}
          >
            {refund.isPending ? 'Refunding…' : 'Confirm refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({
  label, value, sublabel, accent,
}: {
  label: string;
  value: string;
  sublabel: string;
  accent: 'emerald' | 'red' | 'slate';
}) {
  const border = {
    emerald: 'border-emerald-200/50',
    red: 'border-red-200/50',
    slate: 'border-slate-200',
  }[accent];
  const valueColor = {
    emerald: 'text-emerald-700',
    red: 'text-red-700',
    slate: 'text-foreground',
  }[accent];
  return (
    <Card className={cn('relative overflow-hidden', border)}>
      <CardContent className="py-5">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </div>
        <div className={cn('mt-1 text-2xl font-semibold tabular-nums', valueColor)}>
          {value}
        </div>
        {sublabel && (
          <div className="mt-1 text-xs text-muted-foreground">{sublabel}</div>
        )}
      </CardContent>
    </Card>
  );
}
